import "core-js/modules/es.array.push.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.find.js";
import "core-js/modules/es.iterator.map.js";
import "core-js/modules/es.iterator.reduce.js";
import "core-js/modules/es.iterator.some.js";
import { defineComponent as _defineComponent } from 'vue';
import { isEqual, sortBy } from "lodash-es";
import { computed, ref, watch } from "vue";
import { useInputField } from "@common/composables/useInputField";
import icons from "@common/icons";
import { useSlots } from 'vue';
import BaseDropdown from "../BaseDropdown.vue";
import BaseListGroup from "../lists/BaseListGroup.vue";
// ============================================================================
// PROPS
// ============================================================================
export default /*#__PURE__*/_defineComponent({
  __name: 'BaseSelectField',
  props: {
    name: null,
    label: null,
    errors: {
      default: () => []
    },
    note: null,
    noteClasses: null,
    required: {
      type: Boolean
    },
    value: {
      type: [String, Number, Boolean, Array]
    },
    emptyLabel: {
      default: "--"
    },
    emptyValue: {
      type: [String, Number, Boolean]
    },
    allowNulls: null,
    items: {
      default: () => []
    },
    autoSort: {
      type: Boolean,
      default: true
    },
    max: {
      default: 1
    },
    min: {
      default: 1
    },
    placeholder: null,
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean
    },
    inline: {
      type: Boolean
    },
    rounded: null,
    size: null
  },
  emits: ["input", "blur"],
  setup(__props, {
    expose,
    emit
  }) {
    const props = __props;
    /**
     * BaseSelectField - A flexible dropdown select component
     *
     * Supports single and multi-select modes, grouped options, custom icons,
     * and integrates with the form field system via useInputField composable.
     */
    // ============================================================================
    // IMPORTS
    // ============================================================================
    // External libraries
    const slots = useSlots();
    // ============================================================================
    // TYPES & INTERFACES
    // ============================================================================
    /** Represents a selectable item in the dropdown */
    // ============================================================================
    // EMITS
    // ============================================================================
    // ============================================================================
    // COMPOSABLES
    // ============================================================================
    const {
      input,
      innerValue,
      hasValue,
      hasErrors,
      focus
    } = useInputField({
      props,
      emit
    });
    // ============================================================================
    // CONSTANTS
    // ============================================================================
    /** Default group key for ungrouped items */
    const DEFAULT_GROUP_KEY = "___DEFAULT___";
    // ============================================================================
    // TEMPLATE REFS
    // ============================================================================
    const dropdown = ref();
    const list = ref();
    // ============================================================================
    // REACTIVE STATE
    // ============================================================================
    /** Currently focused item ID for keyboard navigation */
    const focusedItemId = ref();
    // ============================================================================
    // COMPUTED PROPERTIES - Selection Mode
    // ============================================================================
    /** Whether multi-select mode is enabled (max > 1 allows multiple selections) */
    const isMulti = computed(() => props.max > 1);
    // ============================================================================
    // COMPUTED PROPERTIES - Options Processing
    // ============================================================================
    /** Groups items by their group property for sectioned display */
    const groupedOptions = computed(() => {
      let groups = props.items.reduce((groups, item, index) => {
        const groupKey = item.group ?? DEFAULT_GROUP_KEY;
        const groupedItem = {
          ...item,
          id: getItemKey(groupKey, index)
        };
        if (groups[groupKey]) {
          groups[groupKey].push(groupedItem);
        } else {
          groups[groupKey] = [groupedItem];
        }
        return groups;
      }, {});
      let results = [];
      if (props.autoSort) {
        results = sortBy(Object.keys(groups)).map(key => {
          return {
            key,
            items: sortBy(groups[key], 'text')
          };
        });
      } else {
        results = Object.keys(groups).map(key => {
          return {
            key,
            items: groups[key]
          };
        });
      }
      return results;
    });
    /** The currently selected option (single-select mode only) */
    const currentOption = computed(() => {
      return props.items.find(item => isEqual(item.value, innerValue.value));
    });
    // ============================================================================
    // COMPUTED PROPERTIES - Display
    // ============================================================================
    /** Dynamic CSS classes for the input element based on rounding prop */
    const inputClasses = computed(() => {
      const classes = [];
      if (props.rounded === false) {
        classes.push("rounded-0");
      } else if (typeof props.rounded === "string") {
        classes.push(`rounded-${props.rounded}`);
      }
      return classes.join(" ");
    });
    /** Display text for the select field (selected item(s) or placeholder) */
    const fieldLabel = computed(() => {
      if (Array.isArray(innerValue.value)) {
        const selectedValues = innerValue.value;
        const selectedOptions = props.items.filter(item => selectedValues.some(val => isEqual(item.value, val)));
        return selectedOptions.length > 0 ? selectedOptions.map(opt => opt.text).join(", ") : props.emptyLabel;
      }
      return currentOption.value?.text ?? props.emptyLabel;
    });
    /** Determines which note to display - option description takes precedence if different from text */
    const noteToUse = computed(() => {
      const description = currentOption.value?.description;
      const text = currentOption.value?.text;
      const hasUniqueDescription = description?.length && description !== text;
      return hasUniqueDescription ? description : props.note;
    });
    // ============================================================================
    // WATCHERS
    // ============================================================================
    /**
     * Synchronizes innerValue format when switching between single and multi-select modes.
     * Converts array to single value or vice versa as needed.
     */
    watch(isMulti, isMultiSelect => {
      const currentValue = innerValue.value;
      if (isMultiSelect && !Array.isArray(currentValue)) {
        // Switching to multi-select: convert to array
        innerValue.value = [];
      } else if (!isMultiSelect && Array.isArray(currentValue)) {
        // Switching to single-select: take first value
        innerValue.value = currentValue[0];
      }
    }, {
      immediate: true
    });
    // ============================================================================
    // METHODS - Selection
    // ============================================================================
    /** Toggles or sets the selected value(s) */
    const select = val => {
      if (Array.isArray(innerValue.value)) {
        // Multi-select: toggle the value
        const existingIndex = innerValue.value.findIndex(v => isEqual(v, val));
        if (existingIndex > -1) {
          innerValue.value.splice(existingIndex, 1);
        } else {
          innerValue.value.push(val);
        }
      } else {
        // Single-select: set the value
        innerValue.value = val;
      }
      // Close dropdown and refocus input in single-select mode
      if (!isMulti.value) {
        dropdown.value?.toggleMenu(false);
        input.value?.focus();
      }
    };
    /** Checks if a value is currently selected */
    const isSelected = val => {
      if (Array.isArray(innerValue.value)) {
        return innerValue.value.some(v => isEqual(v, val));
      }
      return isEqual(val, innerValue.value);
    };
    // ============================================================================
    // METHODS - Utilities
    // ============================================================================
    /** Generates a unique key for list items based on group and index */
    const getItemKey = (groupKey, index) => {
      return `${groupKey}_item_${index}`;
    };
    /** Returns event handlers for list items */
    const getItemEvents = value => ({
      click: () => select(value)
    });
    // ============================================================================
    // EXPOSE
    // ============================================================================
    expose({
      focus,
      hasValue
    });
    return {
      __sfc: true,
      slots,
      props,
      emit,
      input,
      innerValue,
      hasValue,
      hasErrors,
      focus,
      DEFAULT_GROUP_KEY,
      dropdown,
      list,
      focusedItemId,
      isMulti,
      groupedOptions,
      currentOption,
      inputClasses,
      fieldLabel,
      noteToUse,
      select,
      isSelected,
      getItemKey,
      getItemEvents,
      icons,
      BaseDropdown,
      BaseListGroup
    };
  }
});